import { fetchJsonCached, getCachedJson } from './portalQueryCache';
import { TOOLS_CACHE_URL } from './platformAccessSync';

let launchOverlayEl = null;
let dataAreaModalApi = null;
let overlayLifecycleBound = false;

function bindLaunchOverlayLifecycle() {
  if (overlayLifecycleBound || typeof window === 'undefined') return;
  overlayLifecycleBound = true;

  // Back/forward (incl. BFCache) restores the previous document with the overlay still in the DOM.
  const clear = () => hidePlatformLaunchOverlay();
  window.addEventListener('pageshow', clear);
  window.addEventListener('pagehide', clear);
  window.addEventListener('popstate', clear);
}

function showPlatformLaunchOverlay() {
  bindLaunchOverlayLifecycle();
  if (launchOverlayEl && document.body.contains(launchOverlayEl)) return;
  // Stale node after BFCache restore — drop it and recreate.
  hidePlatformLaunchOverlay();
  const el = document.createElement('div');
  el.className = 'platform-launch-overlay';
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.innerHTML = '<div class="platform-launch-overlay-card"><span class="spin platform-launch-spinner" aria-hidden="true"></span><span>Opening platform…</span></div>';
  document.body.appendChild(el);
  launchOverlayEl = el;
}

function hidePlatformLaunchOverlay() {
  if (launchOverlayEl) {
    launchOverlayEl.remove();
    launchOverlayEl = null;
  }
  // BFCache / history restore can leave overlay nodes while the module var is null.
  if (typeof document !== 'undefined') {
    document.querySelectorAll('.platform-launch-overlay').forEach((node) => node.remove());
  }
}

// Ensure Back/BFCache clears a leftover overlay even if this module was already loaded.
bindLaunchOverlayLifecycle();

/**
 * Register a React modal host for multi-DataArea selection.
 * @param {(payload: { tool: object, options: Array<{dataAreaId:string,url:string}>, onPick: (opt)=>void, onCancel: ()=>void } | null) => void} openFn
 */
export function registerDataAreaLaunchModal(openFn) {
  dataAreaModalApi = typeof openFn === 'function' ? openFn : null;
  return () => {
    if (dataAreaModalApi === openFn) dataAreaModalApi = null;
  };
}

function pickDataAreaOption(tool, options) {
  return new Promise((resolve) => {
    if (typeof dataAreaModalApi !== 'function') {
      // Never auto-pick a company/country when multi-choice is required.
      resolve(null);
      return;
    }
    dataAreaModalApi({
      tool,
      options,
      onPick: (option) => resolve(option || null),
      onCancel: () => resolve(null),
    });
  });
}

export function getPlatformScreenshotSources(tool) {
  const key = String(tool?.key || '').trim().toLowerCase();
  if (!key) return null;
  return {
    primary: `/platform-screenshots/${encodeURIComponent(key)}.jpg`,
    fallback: '/platform-screenshots/_default.jpg',
  };
}

export function getPlatformLogoSources(tool) {
  const fromApi = String(tool?.logoUrl || '').trim();
  if (fromApi) {
    return {
      primary: fromApi,
      fallback: '/platform-logos/_default.png',
    };
  }

  const key = String(tool?.key || '').trim().toLowerCase();
  if (!key) return null;
  return {
    primary: `/platform-logos/${encodeURIComponent(key)}.png`,
    fallback: '/platform-logos/_default.png',
  };
}

function normalizeLaunchUrl(raw) {
  const rawUrl = String(raw || '').trim();
  if (!rawUrl) return '';
  if (!rawUrl.startsWith('http://') && !rawUrl.startsWith('https://') && !rawUrl.startsWith('/')) {
    return `/${rawUrl}`;
  }
  return rawUrl;
}

function appendDataAreaQuery(url, dataAreaId) {
  const base = String(url || '').trim();
  const area = String(dataAreaId || '').trim();
  if (!base || !area) return base;
  try {
    // Prefer URL API for absolute URLs.
    if (/^https?:\/\//i.test(base)) {
      const u = new URL(base);
      u.searchParams.set('dataAreaId', area);
      return u.toString();
    }
  } catch {
    // fall through
  }
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}dataAreaId=${encodeURIComponent(area)}`;
}

function getToolDataAreaOptions(tool) {
  const rows = Array.isArray(tool?.dataAreaUrls)
    ? tool.dataAreaUrls
    : Array.isArray(tool?.DataAreaUrls)
      ? tool.DataAreaUrls
      : [];
  return rows
    .map((row) => ({
      dataAreaId: String(row?.dataAreaId || row?.DataAreaId || '').trim(),
      url: String(row?.url || row?.Url || '').trim(),
    }))
    .filter((row) => row.dataAreaId && row.url);
}

/**
 * Build fallback options when server omitted dataAreaUrls but reported multiple userDataAreas.
 */
function buildOptionsFromUserAreas(tool, userDataAreas) {
  const baseUrl = String(tool?.url || tool?.Url || '').trim();
  if (!baseUrl) return [];
  const areas = Array.isArray(userDataAreas) ? userDataAreas : [];
  return areas
    .map((da) => String(da || '').trim())
    .filter(Boolean)
    .map((dataAreaId) => ({
      dataAreaId,
      url: appendDataAreaQuery(baseUrl, dataAreaId),
    }));
}

function readUserDataAreas(payload) {
  if (Array.isArray(payload?.userDataAreas)) return payload.userDataAreas;
  if (Array.isArray(payload?.UserDataAreas)) return payload.UserDataAreas;
  return [];
}

function pickToolFromToolsPayload(payload, code) {
  const list = Array.isArray(payload?.tools) ? payload.tools : [];
  return list.find((row) => String(row?.key || row?.Key || '').trim().toLowerCase() === code) || null;
}

function toolRequiresDataAreaPick(tool, options) {
  if (options.length > 1) return true;
  if (tool?.requiresDataAreaPick === true || tool?.RequiresDataAreaPick === true) return true;
  return false;
}

/**
 * Resolve launch metadata with minimal latency:
 * 1) in-memory /api/tools cache from launcher (preferred)
 * 2) short-TTL cached fetch only when multi-company pick may be needed
 * Never block a single-URL launch on a full tools roundtrip.
 */
async function resolveLaunchTool(tool) {
  const code = String(tool?.key || tool?.Key || '').trim().toLowerCase();
  if (!code) return { tool, userDataAreas: [] };

  const localOptions = getToolDataAreaOptions(tool);
  const localUrl = normalizeLaunchUrl(localOptions[0]?.url || tool?.url || tool?.Url);
  const needsCompanyPick = toolRequiresDataAreaPick(tool, localOptions);

  // Fast path: single known URL, no multi-DataArea gate → launch immediately.
  if (!needsCompanyPick && localOptions.length <= 1 && localUrl) {
    const cached = getCachedJson(TOOLS_CACHE_URL);
    return {
      tool,
      userDataAreas: readUserDataAreas(cached),
    };
  }

  const applyPayload = (payload) => {
    const fresh = pickToolFromToolsPayload(payload, code);
    const userDataAreas = readUserDataAreas(payload);
    if (!fresh) return { tool, userDataAreas };
    return { tool: { ...tool, ...fresh }, userDataAreas };
  };

  // Warm cache from PlatformLauncher / ToolsPage (15s TTL).
  const cached = getCachedJson(TOOLS_CACHE_URL);
  if (cached) {
    const fromCache = applyPayload(cached);
    const cacheOptions = getToolDataAreaOptions(fromCache.tool);
    if (cacheOptions.length > 1 || !toolRequiresDataAreaPick(fromCache.tool, cacheOptions)) {
      return fromCache;
    }
  }

  try {
    const data = await fetchJsonCached(TOOLS_CACHE_URL, { ttlMs: 15_000 });
    return applyPayload(data);
  } catch {
    return { tool, userDataAreas: readUserDataAreas(cached) };
  }
}

async function launchExternalUrl(tool, url) {
  const platformCode = String(tool?.key || '').trim().toLowerCase();
  const isHkClaim = /hkclaim/i.test(String(tool?.key || '')) || /hk\s*claim/i.test(String(tool?.name || ''));
  const ssoPlatform = platformCode || (isHkClaim ? 'hkclaim' : '');
  if (!ssoPlatform) {
    window.location.assign(url);
    return true;
  }

  showPlatformLaunchOverlay();
  const startFallback = `/api/sso/start?platform=${encodeURIComponent(ssoPlatform)}&returnUrl=${encodeURIComponent(url)}`;

  // Prefer XHR issue-code → direct platform Consume (one navigation).
  // Classic /api/sso/start is a full document hop + 302 and feels slower under "Opening platform…".
  try {
    const resp = await fetch('/api/sso/issue-code', {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        platformCode: ssoPlatform,
        returnUrl: url,
      }),
    });

    if (resp.status === 403) {
      hidePlatformLaunchOverlay();
      window.location.assign(`/access-denied?platform=${encodeURIComponent(ssoPlatform)}`);
      return true;
    }

    if (resp.status === 401) {
      window.location.assign(startFallback);
      return true;
    }

    if (resp.ok) {
      const data = await resp.json();
      const redirectUrl = String(data?.redirectUrl || '').trim();
      if (redirectUrl) {
        window.location.assign(redirectUrl);
        return true;
      }
    }
  } catch {
    // fall through to start
  }

  window.location.assign(startFallback);
  return true;
}

/**
 * Open a platform tool.
 * When the user has 2+ DataAreaIds (e.g. multi-group), shows the company modal
 * and only redirects after an explicit pick.
 */
export async function openPlatformTool(tool, navigate) {
  if (!tool) return false;

  // Show feedback immediately for external SSO launches (before any network).
  const provisionalUrl = normalizeLaunchUrl(
    getToolDataAreaOptions(tool)[0]?.url || tool.url || tool.Url,
  );
  const looksExternal = /^https?:\/\//i.test(provisionalUrl);
  if (looksExternal && !toolRequiresDataAreaPick(tool, getToolDataAreaOptions(tool))) {
    showPlatformLaunchOverlay();
  }

  // 1) Resolve multi-company launch metadata only when needed.
  const resolved = await resolveLaunchTool(tool);
  const launchTool = resolved.tool;
  let options = getToolDataAreaOptions(launchTool);

  // Client-side safety net: multi userDataAreas → always offer pick.
  if (options.length < 2 && Array.isArray(resolved.userDataAreas) && resolved.userDataAreas.length > 1) {
    const synthetic = buildOptionsFromUserAreas(launchTool, resolved.userDataAreas);
    if (synthetic.length > 1) {
      options = synthetic;
    }
  }

  let chosenUrl = '';

  // 2) Gate: multi-DataArea → modal first; never redirect with default Platforms.Url.
  if (toolRequiresDataAreaPick(launchTool, options) || options.length > 1) {
    hidePlatformLaunchOverlay();
    if (options.length < 2) {
      // Server said multi-pick but list is incomplete — do not redirect blindly.
      return false;
    }
    const picked = await pickDataAreaOption(launchTool, options);
    if (!picked) {
      return false;
    }
    chosenUrl = normalizeLaunchUrl(picked.url);
  } else if (options.length === 1) {
    chosenUrl = normalizeLaunchUrl(options[0].url);
  } else {
    chosenUrl = normalizeLaunchUrl(launchTool.url || launchTool.Url);
  }

  if (!chosenUrl) {
    hidePlatformLaunchOverlay();
    return false;
  }

  // 3) Redirect only after the company/country decision is final.
  if (chosenUrl.startsWith('http://') || chosenUrl.startsWith('https://')) {
    return launchExternalUrl(launchTool, chosenUrl);
  }

  hidePlatformLaunchOverlay();
  if (typeof navigate === 'function') {
    navigate(chosenUrl);
    return true;
  }

  window.location.assign(chosenUrl);
  return true;
}
